/**
 * THE ONE STATEFUL COMPONENT IN THE COMPOSITION.
 * =============================================
 *
 * Everything else under `src/app/` is either a pure function or a pure component. This file holds the
 * flow position, the lookup draft, the in-flight lookup and the last lookup result, and it owns the two
 * side effects the composition performs: `lookupKiosk` (a read) and `enqueueSubmission` (the durable
 * write).
 *
 * **`submit` NEVER TOUCHES THE NETWORK, AND THERE IS NO ONLINE BRANCH TO GET WRONG.** It builds the
 * outbox row and calls `enqueue`, which resolves only once the IndexedDB transaction has COMMITTED. So
 * submitting works identically with the phone in a lift, in a basement, or in flight mode — the offline
 * case is not a special case, it is the only case. If the write itself fails, the error propagates to
 * `FormRenderer`, whose own comment is right about what that means: a failure there is local storage,
 * not connectivity, and it must not be reported as "check your connection".
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { SubmissionPayload } from '@/forms';
import { describeSessionRunway } from '@/auth/phase';
import {
  listInterventions,
  type ApiOutcome,
  type InterventionListItem,
  type InterventionListPage,
} from '@/core/api';
import { useAuthPhase } from '@/ui/useAuthPhase';
import { useOnline, useOutbox } from '@/ui/useOutbox';
import { flowReducer, INITIAL_SCREEN, NO_KIOSK, screenPath, type KioskContext } from './flow';
import {
  contextFromLookup,
  contextFromTypedKid,
  isWellFormedKid,
  lookupKiosk,
  normaliseKid,
  type LookupResult,
} from './kiosk';
import {
  HomeScreen,
  JobFormScreen,
  JobListScreen,
  JobTypeScreen,
  KioskLookupScreen,
  QueuedScreen,
} from './screens';
import { enqueueSubmission } from './submission';

export interface WorkFlowProps {
  /** Lets the shell's bottom nav be driven from inside the flow (the "open the outbox" button). */
  readonly onOpenOutbox?: () => void;
  /**
   * Reports the flow's current hash path (`jobs`, `jobs/form`, …) upward, so the shell's
   * Jobs tab can restore THIS position instead of clobbering the hash with `#/jobs` — which
   * the hash listener below would read as a back gesture. Position only, never state.
   */
  readonly onPathChange?: (path: string) => void;
}

export function WorkFlow({ onOpenOutbox, onPathChange }: WorkFlowProps): JSX.Element {
  const [screen, dispatch] = useReducer(flowReducer, INITIAL_SCREEN);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [jobs, setJobs] = useState<ApiOutcome<InterventionListPage> | null>(null);
  /**
   * H6 — the job-list search box. Held here rather than in `JobListScreen` because `screens.tsx`
   * is hook-free by contract; the screen filters and renders, this component remembers. It
   * deliberately survives picking a job and coming Back — a technician working through "everything
   * at DEN" should not retype the query per job.
   */
  const [jobSearch, setJobSearch] = useState('');
  /**
   * When {@link jobs} was read. Kept beside the outcome rather than derived at render, because it
   * travels into every prefilled answer's provenance and `InterventionListPage._meta` carries no
   * read time of its own — see `FormHost`'s `interventionFetchedAt`.
   */
  const [jobsFetchedAt, setJobsFetchedAt] = useState(0);
  const { counts } = useOutbox();
  const online = useOnline();
  /**
   * D5 — the 10-hour warning, surfaced where the travel decision is made. The condition (signed in
   * AND near the end of the refresh window) lives in `describeSessionRunway`, a pure function of
   * (phase, now); this component only supplies the live phase and the clock. `null` almost always —
   * a technician mid-window sees nothing, and every not-signed-in phase already has its own surface
   * (`SignInBanner` in the shell), which this must not duplicate.
   *
   * Computed at render, no ticking timer: the phase subscription re-renders on every token refresh
   * (one per 15 minutes), which is a finer grain than a two-hour threshold needs.
   */
  const sessionRunway = describeSessionRunway(useAuthPhase().phase, Date.now());

  /**
   * D4 — THE OS BACK GESTURE STEPS BACK THROUGH THE FLOW.
   * ====================================================
   *
   * Read the corrected header of `flow.ts` first: the hash carries the flow's POSITION and
   * never its STATE. Three pieces, one direction each:
   *
   *  · **Forward** transitions PUSH `#/${screenPath(screen)}` (the effect below, when the
   *    change did not come from a pop). One history entry per step is what makes one back
   *    gesture mean one step.
   *  · **A pop** (back/forward gesture) arrives as `hashchange` with a hash in the `#/jobs`
   *    family that differs from the current screen's path. It maps to ONE `back` dispatch —
   *    the same transition the on-screen Back button makes — and never to a parse of the
   *    hash. Our own pushes also fire `hashchange`, but by then `screen` has already
   *    changed, so the values match and the listener ignores them.
   *  · **Divergence is corrected, not chased.** The reducer's `back` is not always the
   *    previous history entry (`queued` goes home). When a pop lands somewhere the reducer
   *    did not, `replaceState` rewrites the hash to the truth without minting an entry —
   *    so the correction cannot itself become a step.
   *
   * A cold start into `#/jobs/<deep>` lands on `home` (a fresh process has NO flow state —
   * the exact failure the original flow.ts note ruled out) and the first-mount effect
   * REPLACES the hash rather than pushing, so the corrected URL does not add an entry.
   */
  const fromPop = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    const onHashChange = (): void => {
      const hash = globalThis.location?.hash ?? '';
      if (!hash.startsWith('#/jobs')) {
        return; // Another view's territory; AppShell owns it.
      }
      if (hash === `#/${screenPath(screen)}`) {
        return; // Our own push echoing back, or a pop that already matches.
      }
      fromPop.current = true;
      dispatch({ kind: 'back' });
    };
    globalThis.addEventListener('hashchange', onHashChange);
    return () => globalThis.removeEventListener('hashchange', onHashChange);
  }, [screen]);

  useEffect(() => {
    const path = screenPath(screen);
    onPathChange?.(path);
    const expected = `#/${path}`;
    const current = globalThis.location?.hash ?? '';
    // A hash outside the jobs family (outbox/settings open, at mount or any other time) is
    // left alone ENTIRELY — the shell owns it, and "correcting" it here would yank the user
    // off the view the hash names. The flow stays mounted-but-hidden on those views, so its
    // screen cannot change while this branch is the live one.
    const inFamily = current === '' || current === '#/' || current.startsWith('#/jobs');
    if (!inFamily || current === expected) {
      fromPop.current = false;
      return;
    }
    if (!mounted.current || fromPop.current) {
      // First mount (possibly a deep link) or a pop the reducer resolved differently:
      // correct without creating an entry.
      fromPop.current = false;
      globalThis.history?.replaceState(null, '', expected);
    } else {
      // A forward transition while the flow's view is active: one entry per step.
      globalThis.location.hash = expected;
    }
  }, [screen, onPathChange]);

  /**
   * H8 — EVERY SCREEN CHANGE RESETS SCROLL AND MOVES FOCUS TO THE NEW SCREEN'S TITLE.
   * =================================================================================
   *
   * Field feedback 8: picking a job left the viewport where the tap happened, so the transition
   * read as a no-op — the form buttons the technician must reach sat below the fold of the new
   * screen. A whole-screen reset is used rather than targeting one button: it fixes every
   * transition at once (forward, on-screen Back, and the OS back gesture alike) and cannot strand
   * focus mid-page.
   *
   * Focus moves WITH the scroll, so the change is announced rather than being visual-only: the
   * new screen's `.section-title` heading (every screen in `screens.tsx` renders one, or the
   * `.fx-screen` section itself with its `aria-label` where a screen has no heading) is given
   * `tabindex="-1"` here and focused. Doing it from this effect keeps `screens.tsx` pure — the
   * screens stay hook-free and unchanged, per that file's header.
   *
   * `prefers-reduced-motion` is respected: `reduce` (and any environment with no `matchMedia`,
   * where the preference cannot be read) gets an instant jump, everyone else a smooth scroll.
   * Keyed on the reducer's `screen` object — a new object per transition, the same object across
   * unrelated re-renders (jobs arriving, busy toggling), so mid-screen work never yanks the
   * viewport. The first-mount guard reuses `mounted`: page load is not a screen change, and
   * stealing focus from the shell on load would be its own accessibility defect.
   */
  useEffect(() => {
    if (!mounted.current) return;
    const doc = globalThis.document;
    const target = doc?.querySelector<HTMLElement>('.fx-screen .section-title') ?? doc?.querySelector<HTMLElement>('.fx-screen');
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      // preventScroll: the scroll below is the one deliberate movement; two competing ones judder.
      target.focus({ preventScroll: true });
    }
    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? true;
    globalThis.scrollTo?.({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [screen]);

  useEffect(() => {
    mounted.current = true;
  }, []);

  const runLookup = useCallback(async (): Promise<void> => {
    const kid = normaliseKid(draft);
    if (!isWellFormedKid(kid)) return;
    setBusy(true);
    setResult(null);
    try {
      setResult(await lookupKiosk(kid));
    } finally {
      setBusy(false);
    }
  }, [draft]);

  const useKiosk = useCallback((kiosk: KioskContext): void => {
    dispatch({ kind: 'kiosk', kiosk });
  }, []);

  const loadJobs = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const outcome = await listInterventions();
      // The timestamp is taken AFTER the await resolves, so it is when the answer arrived rather
      // than when the request left. Set together with the outcome so the two cannot disagree.
      setJobsFetchedAt(Date.now());
      setJobs(outcome);
    } finally {
      setBusy(false);
    }
  }, []);

  /**
   * A job was picked off the work list.
   *
   * The kiosk lookup is attempted but is **never a gate**: it is the same rule the manual path
   * follows — a missing kiosk record must not become a missing day's work. Three outcomes, all
   * carried honestly:
   *
   *  · the read succeeded → `source: 'server'` (or `'session'`), full kiosk payload;
   *  · the read failed and the job has a KID → `source: 'typed-only'`, which is the accurate label:
   *    we have the identifier and not the record. It came off the dispatch rather than a thumb, but
   *    the epistemic position is identical and inventing a fourth `source` value to flatter it would
   *    let a screen present it as knowledge;
   *  · the job has no KID at all — 50.7% of Survey-type interventions — → `NO_KIOSK`, which is a
   *    whole valid starting point rather than a degraded one.
   *
   * In every one of the three, the intervention still reaches the form, so the location, the case
   * and the installation id are prefilled even when the kiosk read fails completely.
   */
  const useJob = useCallback(
    async (item: InterventionListItem): Promise<void> => {
      const fetchedAt = jobsFetchedAt === 0 ? Date.now() : jobsFetchedAt;
      const jobDispatch = { intervention: item, fetchedAt };

      if (item.kioskId === null || !isWellFormedKid(normaliseKid(item.kioskId))) {
        dispatch({ kind: 'kiosk', kiosk: NO_KIOSK, dispatch: jobDispatch });
        return;
      }

      setBusy(true);
      try {
        const lookup = await lookupKiosk(item.kioskId);
        dispatch({
          kind: 'kiosk',
          kiosk:
            lookup.kind === 'found'
              ? contextFromLookup(lookup)
              : contextFromTypedKid(item.kioskId),
          dispatch: jobDispatch,
        });
      } finally {
        setBusy(false);
      }
    },
    [jobsFetchedAt],
  );

  /**
   * Continue with no kiosk record.
   *
   * A well-formed KID that could not be resolved is still recorded — it is what the dispatch says, and
   * it is what lets the submission be matched to a kiosk later. It is carried as `source:
   * 'typed-only'`, so no screen can mistake it for a record we read.
   */
  const skipKiosk = useCallback((): void => {
    const kid = normaliseKid(draft);
    dispatch({ kind: 'kiosk', kiosk: isWellFormedKid(kid) ? contextFromTypedKid(kid) : NO_KIOSK });
  }, [draft]);

  const submit = useCallback(
    async (payload: SubmissionPayload): Promise<void> => {
      if (screen.name !== 'form') {
        // Unreachable through the UI; if it ever happens, refusing is better than queueing a
        // submission with no idea which kiosk it belongs to.
        throw new Error('not on a form screen');
      }
      const queued = await enqueueSubmission(payload, screen.kiosk);
      dispatch({ kind: 'queued', outboxId: queued.id, idempotencyKey: queued.idempotencyKey });
    },
    [screen],
  );

  switch (screen.name) {
    case 'home':
      return (
        <HomeScreen
          counts={counts}
          onOpenJobs={() => {
            dispatch({ kind: 'jobs' });
            // Fetched on entry rather than behind a second tap. A technician opening their work list
            // wants the list, and the request is idempotent.
            void loadJobs();
          }}
          onWorkOnKiosk={() => dispatch({ kind: 'lookup' })}
          onNoKiosk={() => dispatch({ kind: 'kiosk', kiosk: NO_KIOSK })}
        />
      );

    case 'jobs':
      return (
        <JobListScreen
          busy={busy}
          outcome={jobs}
          search={jobSearch}
          onSearch={setJobSearch}
          sessionRunway={sessionRunway}
          onRetry={() => void loadJobs()}
          onPick={(item) => void useJob(item)}
          onBack={() => dispatch({ kind: 'back' })}
        />
      );

    case 'lookup':
      return (
        <KioskLookupScreen
          draft={draft}
          onDraft={setDraft}
          onLookup={() => void runLookup()}
          busy={busy}
          result={result}
          onUse={useKiosk}
          onSkip={skipKiosk}
          onBack={() => dispatch({ kind: 'back' })}
        />
      );

    case 'job-types':
      return (
        <JobTypeScreen
          kiosk={screen.kiosk}
          dispatch={screen.dispatch}
          onPick={(schemaId) => dispatch({ kind: 'schema', schemaId })}
          onBack={() => dispatch({ kind: 'back' })}
        />
      );

    case 'form':
      return (
        <JobFormScreen
          kiosk={screen.kiosk}
          schemaId={screen.schemaId}
          dispatch={screen.dispatch}
          submit={submit}
          onBack={() => dispatch({ kind: 'back' })}
        />
      );

    case 'queued':
      return (
        <QueuedScreen
          schemaId={screen.schemaId}
          outboxId={screen.outboxId}
          idempotencyKey={screen.idempotencyKey}
          online={online}
          onHome={() => dispatch({ kind: 'home' })}
          onOpenOutbox={() => onOpenOutbox?.()}
        />
      );
  }
}
